/**
 * Hydrogen Focus Trap Utility
 *
 * Provides reusable focus trapping for modal dialogs and other
 * components that need to constrain keyboard navigation.
 *
 * Usage:
 * - const cleanup = createFocusTrap(container);
 * - cleanup(); // Remove trap and restore focus
 */
/**
 * Selector for focusable elements within a container
 * Follows WCAG 2.1 guidelines for keyboard-accessible elements
 */
export const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');
/**
 * Gets all focusable elements within a container
 */
export function getFocusableElements(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));
}
/**
 * Creates a focus trap within a container element.
 * Traps Tab/Shift+Tab navigation within the container.
 *
 * Edge cases handled:
 * - No focusable elements: Tab is prevented, focus stays on container
 * - Single focusable element: Tab cycles back to the same element
 * - Dynamic content: Re-queries focusable elements on each Tab press
 *
 * @returns Cleanup function to remove the trap and restore focus
 */
export function createFocusTrap(container, options = {}) {
    const { autoFocus = true, restoreFocus = true } = options;
    const previouslyFocused = document.activeElement;
    // Auto-focus the first focusable element
    if (autoFocus) {
        const focusable = getFocusableElements(container);
        const firstFocusable = focusable[0];
        if (firstFocusable) {
            // Use setTimeout to ensure the container is visible before focusing
            setTimeout(() => firstFocusable.focus(), 0);
        }
    }
    const handleKeydown = (e) => {
        if (e.key !== 'Tab')
            return;
        // Re-query focusable elements to handle dynamic content
        const currentFocusable = getFocusableElements(container);
        // No focusable elements: prevent Tab from leaving container
        if (currentFocusable.length === 0) {
            e.preventDefault();
            return;
        }
        const first = currentFocusable[0];
        const last = currentFocusable[currentFocusable.length - 1];
        if (!first || !last)
            return;
        // Shift+Tab from first element wraps to last
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        }
        // Tab from last element wraps to first
        else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    };
    container.addEventListener('keydown', handleKeydown);
    // Focus can leave without a Tab keypress: a click on a still-visible
    // control outside the container moves focus directly. Pull it back so the
    // trap holds by ownership, not by key order (#1509).
    const handleFocusIn = () => {
        if (!container.isConnected)
            return;
        const active = document.activeElement;
        if (active === null || container.contains(active))
            return;
        const firstFocusable = getFocusableElements(container)[0];
        if (firstFocusable)
            firstFocusable.focus();
    };
    // Capture phase: the focus event does not bubble. Guarded so the unit-mock
    // documents this file tests against — which implement neither listener
    // registration nor real focus — keep working unchanged.
    if (typeof document.addEventListener === 'function') {
        document.addEventListener('focus', handleFocusIn, true);
    }
    // Return cleanup function
    return () => {
        container.removeEventListener('keydown', handleKeydown);
        if (typeof document.removeEventListener === 'function') {
            document.removeEventListener('focus', handleFocusIn, true);
        }
        // Duck-type focus restore (unit mocks are plain objects with focus())
        if (restoreFocus &&
            previouslyFocused &&
            typeof previouslyFocused.focus === 'function') {
            previouslyFocused.focus();
        }
    };
}
/**
 * Handles Tab key navigation for focus trapping within an element.
 * This is a lower-level utility for integrating with existing keydown handlers.
 *
 * @returns True if the event was handled (Tab key in container)
 */
export function handleFocusTrapKeydown(event, container) {
    if (event.key !== 'Tab')
        return false;
    const focusable = getFocusableElements(container);
    // No focusable elements: prevent Tab from leaving
    if (focusable.length === 0) {
        event.preventDefault();
        return true;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) {
        event.preventDefault();
        return true;
    }
    // Focus that escaped without a Tab keypress (a click outside, a URL-bar
    // focus) is neither first nor last; wrap it back in instead of letting the
    // keystroke walk the page behind the overlay (#1509).
    const active = document.activeElement;
    if (active !== null && typeof container.contains === 'function' && !container.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return true;
    }
    // Shift+Tab from first element wraps to last
    if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
        return true;
    }
    // Tab from last element wraps to first
    else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
        return true;
    }
    return true;
}
//# sourceMappingURL=focus-trap.js.map